import update from "immutability-helper";
import log from "loglevel";
import { isType, Action } from "typescript-fsa";
import { Map } from "immutable";

import { StateError, NormalisedResult } from "@orm/models";
import * as api from "@api/actions";
import * as music from "@music/actions";
import * as reports from "@reports/actions";
import * as search from "@search/actions";
import * as upload from "@upload/actions";
import * as actions from "../actions";
import {
    AuthDetails,
    AuthInternalState as State,
    RequestsState,
    AuthNormalisedEntities,
    User,
    Organisation,
    Role,
    Permission,
} from "../models";

const getInitialState = () => ({
    accessToken: null,
    authenticated: false,
    user: null,
    entities: {
        organisations: Map<number, Organisation>(),
        permissions: Map<number, Permission>(),
        roles: Map<string, Role>(),
        users: Map<number, User>(),
    },
    requests: {
        loginError: null,
        usersIndexError: null,
        usersIndexLastSuccess: null,
        organisationsIndexLastSuccess: null,
        organisationsIndexError: null,
        rolesIndexLastSuccess: null,
        rolesIndexError: null,
    },
} as State);

function auth(state: State = getInitialState(), action: actions.AuthAction | api.ApiAction): State {
    // State should never be null...
    if (state === null) {
        log.warn("auth state setting to null, resetting to initialState.");
        state = {} as State;
        state = update(state, { $set: getInitialState() });
    }

    if (isType(action, actions.loginSuccess)) {
        // Successful login
        return loginSuccess(state, action);
    }
    else if (isType(action, actions.loginFailure)) {
        // Successful login
        return loginFailure(state, action);
    }
    else if (isType(action, actions.usersIndexSuccess)) {
        // Successful users index
        return usersIndexSuccess(state, action);
    }
    else if (isType(action, actions.usersIndexFailure)) {
        // Unsuccessful users index
        return usersIndexFailure(state, action);
    }
    else if (isType(action, actions.usersStoreSuccess) && action.payload != null) {
        return addUser(state, action.payload);
    }
    // TODO Handle users store failure
    else if (isType(action, actions.usersUpdateSuccess) && action.payload != null) {
        return addUser(state, action.payload);
    }
    // TODO Handle users update failure
    else if (isType(action, actions.usersDestroySuccess) && action.payload != null) {
        return removeUser(state, action.payload);
    }
    // TODO Handle users destroy failure
    else if (isType(action, actions.organisationsIndexSuccess)) {
        // Successful organisations index
        return organisationsIndexSuccess(state, action);
    }
    else if (isType(action, actions.organisationsIndexFailure)) {
        // Unsuccessful organisations index
        return organisationsIndexFailure(state, action);
    }
    else if (isType(action, actions.rolesIndexSuccess)) {
        // Successful roles index
        return rolesIndexSuccess(state, action);
    }
    else if (isType(action, actions.rolesIndexFailure)) {
        // Unsuccessful roles index
        return rolesIndexFailure(state, action);
    }
    else if (isType(action, music.albumsIndexSuccess)) {
        // Successful albumsShow
        return mergeAction(state, action.payload[1].entities);
    }
    else if (isType(action, music.albumsShowSuccess)) {
        // Successful albumsShow
        return mergeAction(state, action.payload.entities);
    }
    else if (isType(action, search.searchSuccess)) {
        return mergeAction(state, action.payload.entities);
    }
    else if (isType(action, upload.bundlesShowSuccess)) {
        // Successful bundlesShow
        return mergeAction(state, action.payload.entities);
    }
    else if (isType(action, upload.bundlesIndexSuccess)) {
        // Successful bundlesIndex
        return mergeAction(state, action.payload[1].entities);
    }
    else if (isType(action, reports.reportsIndexSuccess)) {
        // Successful reportsIndex
        return mergeAction(state, action.payload[1].entities);
    }
    else if (isType(action, actions.logout) || api.isFailedResponseUnauthenticatedNotLogin(action)) {
        // Clear the auth state.
        return update(state, { $set: getInitialState() });
    }
    // Otherwise we do nothing
    return state;
}

function loginSuccess(state: State, action: Action<AuthDetails>): State {
    const { accessToken, userEntities } = action.payload;
    const newState = {
        accessToken,
        authenticated: true,
        entities: {},
        requests: {
            loginError: null,
        },
    } as State;

    // Get the user model in.
    if (userEntities.users.size > 0) {
        newState.user = userEntities.users.first().id;

        // Merge the entity maps
        newState.entities = mergeEntities(state, userEntities);
    }

    // Merge
    return update(state, {
        $merge: newState,
        entities: { $merge: newState.entities },
        requests: { $merge: newState.requests },
    });
}

function loginFailure(state: State, action: Action<StateError>): State {
    const newState = {
        loginError: action.payload,
    } as RequestsState;
    return update(state, { requests: { $merge: newState } });
}

function usersIndexSuccess(
    state: State,
    action: Action<NormalisedResult<number[], AuthNormalisedEntities>>,
): State {
    const { entities } = action.payload;
    // Set up the new state
    const newState = {
        entities: {},
        requests: {
            usersIndexError: null,
            usersIndexLastSuccess: new Date(),
        },
    } as State;
    // Merge the entity maps
    newState.entities = mergeEntities(state, entities);

    // Merge the state itself.
    const nextState = update(state, {
        entities: { $merge: newState.entities },
        requests: { $merge: newState.requests },
    });
    // But overwrite the users.
    if (entities.users != null) {
        return update(nextState, {
            entities: {
                users: {
                    $set: entities.users,
                },
            },
        });
    }
    return nextState;
}

function usersIndexFailure(state: State, action: Action<StateError>): State {
    const newState = {
        requests: {
            usersIndexError: action.payload,
        },
    } as State;
    return update(state, { requests: { $merge: newState.requests } });
}

function organisationsIndexSuccess(
    state: State,
    action: Action<NormalisedResult<number[], AuthNormalisedEntities>>,
): State {
    // Set up the new state
    const newState = {
        entities: {},
        requests: {
            organisationsIndexError: null,
            organisationsIndexLastSuccess: new Date(),
        },
    } as State;
    // Merge the entity maps
    const { organisations } = action.payload.entities;
    if (organisations != null) {
        newState.entities.organisations = state.entities.organisations.merge(organisations);
    }
    // But overwrite the organisations.
    if (organisations != null) {
        newState.entities.organisations = organisations;
    }
    // Finally merge the state itself.
    return update(state, {
        entities: { $merge: newState.entities },
        requests: { $merge: newState.requests },
    });
}

function organisationsIndexFailure(state: State, action: Action<StateError>): State {
    const newState = {
        requests: {
            organisationsIndexError: action.payload,
        },
    } as State;
    return update(state, { requests: { $merge: newState.requests } });
}

function rolesIndexSuccess(
    state: State,
    action: Action<NormalisedResult<string[], AuthNormalisedEntities>>,
): State {
    // Set up the new state
    const newState = {
        entities: {},
        requests: {
            rolesIndexError: null,
            rolesIndexLastSuccess: new Date(),
        },
    } as State;
    // Merge the entity maps
    const { roles } = action.payload.entities;
    if (roles != null) {
        newState.entities.roles = state.entities.roles.merge(roles);
    }
    // Finally merge the state itself.
    return update(state, {
        entities: { $merge: newState.entities },
        requests: { $merge: newState.requests },
    });
}

function rolesIndexFailure(state: State, action: Action<StateError>): State {
    const newState = {
        requests: {
            rolesIndexError: action.payload,
        },
    } as State;
    return update(state, { requests: { $merge: newState.requests } });
}

function addUser(state: State, user: User): State {
    // Add the user to the map
    const newState = {
        entities: {
            users: state.entities.users.set(user.id, user),
        },
    } as State;
    return update(state, { entities: { users: { $set: newState.entities.users } } });
}

function removeUser(state: State, userId: number): State {
    // Add the user to the map
    const newState = {
        entities: {
            users: state.entities.users.delete(userId),
        },
    } as State;
    return update(state, { entities: { users: { $set: newState.entities.users } } });
}

function mergeAction(state: State, entities: AuthNormalisedEntities): State {
    // Set up the new state
    const newState = {
        entities: {},
    } as State;
    // Merge the entity maps
    newState.entities = mergeEntities(state, entities, true);
    // Finally merge the state itself.
    return update(state, {
        $merge: {
            entities: newState.entities,
        },
    });
}

function mergeEntities(
    state: State,
    entities: AuthNormalisedEntities,
    prioritiseOld: boolean = false,
): AuthNormalisedEntities {
    let mergedEntities: AuthNormalisedEntities = update(state.entities, {});

    const { organisations, permissions, roles, users } = entities;
    if (organisations != null) {
        mergedEntities = update(mergedEntities, {
            organisations: { $merge: state.entities.organisations.merge(organisations) },
        });
    }
    if (permissions != null) {
        mergedEntities = update(mergedEntities, {
            permissions: { $merge: state.entities.permissions.merge(permissions) },
        });
    }
    if (roles != null) {
        mergedEntities = update(mergedEntities, {
            roles: { $merge: state.entities.roles.merge(roles) },
        });
    }
    if (users != null) {
        mergedEntities = update(mergedEntities, {
            users: {
                $merge: prioritiseOld
                    ? state.entities.users.mergeWith((previous: User) => {
                        return previous;
                    }, users)
                    : state.entities.users.merge(users),
            },
        });
    }

    return mergedEntities;
}

export { auth, getInitialState };
